import React from 'react';
import FormField from '../shared/FormField';
import CountrySelect, { getCountryName } from '../shared/CountrySelect';
import USStateSelect, { getStateName } from '../shared/USStateSelect';
import ProvinceCitySelect from '../shared/ProvinceCitySelect';
import ZIPCodeInput from '../shared/ZIPCodeInput';
import ChineseZIPCodeInput from '../shared/ChineseZIPCodeInput';
import PhoneInput from '../shared/PhoneInput';
import formDataStorage from '../../utils/formDataStorage';
import { Briefcase, MapPin, Phone, Calendar, DollarSign, FileText, ChevronDown } from 'lucide-react';

// 工作教育信息表单
const WorkEducationForm = ({ data, onChange }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    console.log('WorkEducationForm useEffect - 初始化数据检查');
    const savedData = formDataStorage.loadFormData('work_education');
    console.log('WorkEducationForm useEffect - 已保存数据:', savedData);
    console.log('WorkEducationForm useEffect - 当前props数据:', data);
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('WorkEducationForm useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
  }, [data, onChange]);

  // 职业选项（与官方DS-160表单完全一致）
  const OCCUPATION_OPTIONS = [
    { value: 'A', label: '农业' },
    { value: 'AP', label: '艺术家/表演者' },
    { value: 'B', label: '商业' },
    { value: 'CM', label: '通讯' },
    { value: 'CS', label: '计算机科学' },
    { value: 'C', label: '烹饪/餐饮服务' },
    { value: 'ED', label: '教育' },
    { value: 'EN', label: '工程' },
    { value: 'G', label: '政府' },
    { value: 'H', label: '家庭主妇/主夫' },
    { value: 'LP', label: '法律专业' },
    { value: 'MH', label: '医疗/健康' },
    { value: 'M', label: '军事' },
    { value: 'NS', label: '自然科学' },
    { value: 'N', label: '无业' },
    { value: 'PS', label: '物理科学' },
    { value: 'RV', label: '宗教职业' },
    { value: 'R', label: '研究' },
    { value: 'RT', label: '退休' },
    { value: 'SS', label: '社会科学' },
    { value: 'S', label: '学生' },
    { value: 'O', label: '其他' }
  ];

  // 月份映射
  const MONTHS = [
    { value: '1', label: '01月' },
    { value: '2', label: '02月' },
    { value: '3', label: '03月' },
    { value: '4', label: '04月' },
    { value: '5', label: '05月' },
    { value: '6', label: '06月' },
    { value: '7', label: '07月' },
    { value: '8', label: '08月' },
    { value: '9', label: '09月' },
    { value: '10', label: '10月' },
    { value: '11', label: '11月' },
    { value: '12', label: '12月' }
  ];

  // 获取指定年月的天数
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(year, month, 0).getDate();
  };

  // 生成日期选项
  const generateDayOptions = (year, month) => {
    const maxDays = getDaysInMonth(year, month);
    return Array.from({ length: maxDays }, (_, i) => {
      const day = String(i + 1).padStart(2, '0');
      return { value: day, label: `${day}日` };
    });
  };

  // 验证开始日期是否在生日之后且在当前日期之前
  const validateStartDate = (year, month, day) => {
    if (!year || !month || !day) return '';
    
    // 从Personal1Form获取生日信息
    const personal1Data = formDataStorage.loadFormData('personal_1');
    if (!personal1Data || !personal1Data.date_and_place_of_birth) {
      return '请先在个人信息页面填写出生日期';
    }
    
    const birthData = personal1Data.date_and_place_of_birth;
    if (!birthData.year || !birthData.month || !birthData.day) {
      return '请先在个人信息页面完整填写出生日期';
    }
    
    // 将月份从英文缩写转换为数字
    const monthMap = {
      'JAN': 1, 'FEB': 2, 'MAR': 3, 'APR': 4, 'MAY': 5, 'JUN': 6,
      'JUL': 7, 'AUG': 8, 'SEP': 9, 'OCT': 10, 'NOV': 11, 'DEC': 12
    };
    
    const birthMonthNum = monthMap[birthData.month] || parseInt(birthData.month);
    const startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const birthDate = new Date(parseInt(birthData.year), birthMonthNum - 1, parseInt(birthData.day));
    const today = new Date();
    today.setHours(23, 59, 59, 999); // 设置为当天结束时间
    
    if (startDate <= birthDate) {
      return '开始工作/学习日期必须在出生日期之后';
    }
    
    if (startDate > today) {
      return '开始工作/学习日期不能是未来日期';
    }
    
    return '';
  };

  // 更新开始日期
  const updateStartDate = (field, value) => {
    const currentStartDate = data.start_date || {};
    const newStartDate = { ...currentStartDate, [field]: value };
    
    // 如果修改了年或月，需要检查日期是否有效
    if (field === 'year' || field === 'month') {
      const maxDays = getDaysInMonth(newStartDate.year, newStartDate.month);
      if (newStartDate.day && parseInt(newStartDate.day) > maxDays) {
        newStartDate.day = String(maxDays);
      }
    }
    
    onChange('start_date', newStartDate);
  };

  // 获取职业选项标签
  const getOccupationLabel = (code) => {
    const option = OCCUPATION_OPTIONS.find(opt => opt.value === code);
    return option ? option.label : code;
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">💼</span>
          工作教育信息
        </h3>

        {/* 职业信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Briefcase className="w-5 h-5 mr-2" />
            职业信息
          </h4>

          <div className="space-y-4">
            {/* 主要职业 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                主要职业 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={data.primary_occupation || ''}
                  onChange={(e) => onChange('primary_occupation', e.target.value)}
                  className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer"
                >
                  <option value="">请选择职业...</option>
                  {OCCUPATION_OPTIONS.map(occupation => (
                    <option key={occupation.value} value={occupation.value}>
                      {occupation.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
              
              {data.primary_occupation && (
                <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
                  <div className="text-sm font-medium text-green-800">
                    ✅ 已选择职业: {getOccupationLabel(data.primary_occupation)}
                  </div>
                </div>
              )}
            </div>

            {/* 职业类型说明（OTHER和NOT EMPLOYED时显示） */}
            {(data.primary_occupation === 'O' || data.primary_occupation === 'N') && (
              <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border-l-4 border-yellow-400">
                <FormField
                  label={data.primary_occupation === 'O' ? "请具体说明其他职业类型" : "请具体说明无业状况"}
                  value={data.primary_occupation_explain || ''}
                  onChange={(e) => onChange('primary_occupation_explain', e.target.value)}
                  placeholder={data.primary_occupation === 'O' ? "请详细描述您的具体职业..." : "请详细说明您的无业状况、原因等..."}
                  required
                />
                <div className="mt-2 text-sm text-yellow-700">
                  <strong>注意：</strong> {data.primary_occupation === 'O' 
                    ? '由于您选择了"其他"职业类型，请详细说明您的具体职业。'
                    : '由于您选择了"无业"状态，请详细说明您的具体状况。'
                  }
                </div>
              </div>
            )}

            {/* 雇主或学校名称（退休/家庭主妇时隐藏） */}
            {data.primary_occupation !== 'RT' && data.primary_occupation !== 'N' && data.primary_occupation !== 'O' && data.primary_occupation !== 'H' && (
              <FormField
                label="当前雇主或学校名称"
                value={data.present_employer_or_school_name || ''}
                onChange={(e) => onChange('present_employer_or_school_name', e.target.value)}
                placeholder="TEST COMPANY LTD / BEIJING UNIVERSITY"
                required
              />
            )}
          </div>
        </div>

        {/* 工作地址信息（退休/家庭主妇时隐藏） */}
        {data.primary_occupation !== 'RT' && data.primary_occupation !== 'N' && data.primary_occupation !== 'O' && data.primary_occupation !== 'H' && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <MapPin className="w-5 h-5 mr-2" />
            工作地址信息
          </h4>

          <div className="space-y-4">
            <FormField
              label="街道地址（第一行）"
              value={data.street_address || ''}
              onChange={(e) => onChange('street_address', e.target.value)}
              placeholder="123 Business Street"
              required
            />

            <FormField
              label="街道地址（第二行）"
              value={data.street_address_2 || ''}
              onChange={(e) => onChange('street_address_2', e.target.value)}
              placeholder="Suite 456, Floor 10（可选）"
            />

            {/* 按照正确顺序：国家 → 州/省 → 城市 → 邮政编码 */}
            <CountrySelect
              label="国家"
              value={data.country || ''}
              onChange={(value) => {
                // 国家改变时清空州/省和城市选择
                onChange('country', value);
                onChange('state', '');
                onChange('city', '');
              }}
              required
            />

            {/* 根据国家显示不同的州/省和城市输入组件 */}
            {(() => {
              const country = data.country || '';
              const isChineseRegion = ['CHIN', 'MAC', 'HNK', 'TWAN'].includes(country);

              if (isChineseRegion) {
                return (
                  <ProvinceCitySelect
                    provinceValue={data.state || ''}
                    cityValue={data.city || ''}
                    onProvinceChange={(value) => {
                      onChange('state', value);
                      onChange('city', ''); // 清空城市选择
                    }}
                    onCityChange={(value) => onChange('city', value)}
                  />
                );
              } else if (country === 'USA') {
                // 美国使用专用的州选择器
                return (
                  <div className="space-y-4">
                    <USStateSelect
                      label="州"
                      value={data.state || ''}
                      onChange={(value) => onChange('state', value)}
                      required
                    />
                    <FormField
                      label="城市"
                      value={data.city || ''}
                      onChange={(e) => onChange('city', e.target.value)}
                      placeholder="New York"
                      required
                    />
                  </div>
                );
              } else if (country) {
                // 其他国家
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      label="州/省"
                      value={data.state || ''}
                      onChange={(e) => onChange('state', e.target.value)}
                      placeholder="Beijing / California"
                      required
                    />
                    <FormField
                      label="城市"
                      value={data.city || ''}
                      onChange={(e) => onChange('city', e.target.value)}
                      placeholder="New York"
                      required
                    />
                  </div>
                );
              }
              return null;
            })()}

            {/* 邮政编码放在最后 */}
            {(() => {
              const country = data.country || '';
              const isChineseRegion = ['CHIN', 'MAC', 'HNK', 'TWAN'].includes(country);

              if (isChineseRegion) {
                return (
                  <ChineseZIPCodeInput
                    label="邮政编码"
                    value={data.zip_code || ''}
                    onChange={(value) => onChange('zip_code', value)}
                    required
                    countryCode={country}
                  />
                );
              } else if (country) {
                return (
                  <ZIPCodeInput
                    label="邮政编码"
                    value={data.zip_code || ''}
                    onChange={(value) => onChange('zip_code', value)}
                    required
                  />
                );
              }
              return null;
            })()}
          </div>
        </div>
        )}

        {/* 联系信息（退休/家庭主妇时隐藏） */}
        {data.primary_occupation !== 'RT' && data.primary_occupation !== 'N' && data.primary_occupation !== 'O' && data.primary_occupation !== 'H' && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Phone className="w-5 h-5 mr-2" />
            联系信息
          </h4>

          <PhoneInput
            label="工作电话"
            value={data.phone_number || ''}
            onChange={(value) => onChange('phone_number', value)}
            placeholder="13800138000"
            required
          />
        </div>
        )}

        {/* 开始日期（退休/家庭主妇时隐藏） */}
        {data.primary_occupation !== 'RT' && data.primary_occupation !== 'N' && data.primary_occupation !== 'O' && data.primary_occupation !== 'H' && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Calendar className="w-5 h-5 mr-2" />
            开始日期
          </h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              开始工作/学习日期 <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">年份</label>
                <input
                  type="number"
                  min="1980"
                  max="2030"
                  placeholder="2015"
                  value={(data.start_date && data.start_date.year) || ''}
                  onChange={(e) => updateStartDate('year', e.target.value)}
                  className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-center bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">月份</label>
                <div className="relative">
                  <select
                    value={(data.start_date && data.start_date.month) || ''}
                    onChange={(e) => updateStartDate('month', e.target.value)}
                    className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium"
                  >
                    <option value="">选择月份</option>
                    {MONTHS.map(month => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">日期</label>
                <div className="relative">
                  <select
                    value={(data.start_date && data.start_date.day) || ''}
                    onChange={(e) => updateStartDate('day', e.target.value)}
                    disabled={!data.start_date?.year || !data.start_date?.month}
                    className="w-full h-12 px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    <option value="">选择日期</option>
                    {generateDayOptions(data.start_date?.year, data.start_date?.month).map(day => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* 开始日期验证错误提示 */}
            {(() => {
              const startDate = data.start_date || {};
              const validationError = validateStartDate(startDate.year, startDate.month, startDate.day);
              if (validationError) {
                return (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-start">
                      <div className="w-5 h-5 text-red-600 mt-0.5 mr-2">⚠️</div>
                      <p className="text-sm text-red-800 font-medium">{validationError}</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()} 
          </div>
        </div>
        )}

        {/* 收入信息（退休/家庭主妇时隐藏） */}
        {data.primary_occupation !== 'RT' && data.primary_occupation !== 'N' && data.primary_occupation !== 'O' && data.primary_occupation !== 'H' && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <DollarSign className="w-5 h-5 mr-2" />
            收入信息
          </h4>

          <div className="space-y-4">
            {/* 是否有收入 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                您目前是否有收入？
              </label>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="has_income"
                    value="true"
                    checked={data.has_income === true}
                    onChange={(e) => onChange('has_income', true)}
                    className="mr-3 w-5 h-5 text-purple-600 border-gray-300 focus:ring-purple-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 有收入</span>
                </label>

                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="has_income"
                    value="false"
                    checked={data.has_income === false}
                    onChange={(e) => {
                      onChange('has_income', false);
                      onChange('monthly_income', '');
                    }}
                    className="mr-3 w-5 h-5 text-purple-600 border-gray-300 focus:ring-purple-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 无收入</span>
                </label>
              </div>
            </div>

            {/* 月收入 */}
            {data.has_income && (
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-400">
                <FormField
                  label="月收入（美元）"
                  type="number"
                  value={data.monthly_income || ''}
                  onChange={(e) => onChange('monthly_income', parseInt(e.target.value) || '')}
                  placeholder="3000"
                  required
                />
                
                {data.monthly_income && (
                  <div className="mt-3 p-2 bg-green-100 rounded border-l-4 border-green-400">
                    <p className="text-sm text-green-700">
                      <strong>月收入:</strong> ${data.monthly_income} 美元
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* 职责描述（退休/家庭主妇时隐藏） */}
        {data.primary_occupation !== 'RT' && data.primary_occupation !== 'N' && data.primary_occupation !== 'O' && data.primary_occupation !== 'H' && (
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <FileText className="w-5 h-5 mr-2" />
            职责描述
          </h4>

          <FormField
            label="详细描述您的工作职责或学习内容"
            type="textarea"
            value={data.describe_duties || ''}
            onChange={(e) => onChange('describe_duties', e.target.value)}
            placeholder="请详细描述您的日常工作职责、主要任务或学习内容..."
            required
          />
          <p className="text-xs text-amber-600 -mt-2">请用英文填写</p>
        </div>
        )}

        {/* 预览信息 */}
        {(data.primary_occupation && (data.primary_occupation === 'RT' || data.primary_occupation === 'H' || data.present_employer_or_school_name)) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-l-4 border-purple-400 shadow-sm">
            <h4 className="font-semibold text-purple-800 mb-2">信息预览</h4>
            <div className="text-sm text-purple-700 space-y-1">
              {data.primary_occupation && (
                <p><strong>职业:</strong> {getOccupationLabel(data.primary_occupation)}</p>
              )}
              {(data.primary_occupation === 'O' || data.primary_occupation === 'N') && data.primary_occupation_explain && (
                <p><strong>{data.primary_occupation === 'O' ? '职业说明' : '无业说明'}:</strong> {data.primary_occupation_explain}</p>
              )}
              {data.present_employer_or_school_name && (
                <p><strong>雇主/学校:</strong> {data.present_employer_or_school_name}</p>
              )}
              {data.city && (
                <p><strong>工作地址:</strong> {data.city}, {data.country === 'USA' ? getStateName(data.state) : data.state}, {getCountryName(data.country)}</p>
              )}
              {data.phone_number && <p><strong>工作电话:</strong> {data.phone_number}</p>}
              {data.start_date && data.start_date.year && (
                <p><strong>开始日期:</strong> {data.start_date.year}-{data.start_date.month}-{data.start_date.day}</p>
              )}
              <p><strong>是否有收入:</strong> {data.has_income ? '✅ 是' : '❌ 否'}</p>
              {data.has_income && data.monthly_income && (
                <p><strong>月收入:</strong> ${data.monthly_income} 美元</p>
              )}
              {data.describe_duties && (
                <p><strong>职责描述:</strong> {data.describe_duties.substring(0, 100)}{data.describe_duties.length > 100 ? '...' : ''}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkEducationForm;
